import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { SecondaryButton } from "@/components/brand/SecondaryButton";
import { Seal } from "@/components/brand/Seal";
import { Input } from "@/components/ui/input";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listMyLibrary, signProductDownload } from "@/lib/store.functions";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { BookOpen, X } from "lucide-react";

export const Route = createFileRoute("/store/library")({
  validateSearch: (search: Record<string, unknown>): { welcome?: "1" } => ({
    welcome: search.welcome === "1" || search.welcome === 1 ? "1" : undefined,
  }),
  beforeLoad: async () => {
    const { data } = await supabase.auth.getUser();
    if (!data.user) throw redirect({ to: "/login" });
  },
  component: LibraryPage,
});

type LibProduct = {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  thumbnail_url: string | null;
  product_type: string;
};

function relative(date: string | null) {
  if (!date) return "never";
  const diff = Date.now() - new Date(date).getTime();
  const d = Math.floor(diff / 86400000);
  if (d === 0) return "today";
  if (d === 1) return "yesterday";
  if (d < 30) return `${d} days ago`;
  return new Date(date).toLocaleDateString();
}

function LibraryPage() {
  const fetchLibrary = useServerFn(listMyLibrary);
  const sign = useServerFn(signProductDownload);
  const searchParams = Route.useSearch();
  const { data: items, isLoading } = useQuery({
    queryKey: ["store", "library"],
    queryFn: () => fetchLibrary(),
  });
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<"recent" | "az" | "category">("recent");
  const [hasClientRow, setHasClientRow] = useState<boolean | null>(null);
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof document === "undefined") return false;
    return document.cookie
      .split("; ")
      .some((c) => c.startsWith("ct_library_welcome_dismissed="));
  });

  useEffect(() => {
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return;
      const { data: c } = await supabase
        .from("clients")
        .select("id")
        .eq("user_id", u.user.id)
        .maybeSingle();
      setHasClientRow(Boolean(c));
    })();
  }, []);

  const showWelcome =
    !dismissed &&
    (searchParams.welcome === "1" ||
      (items !== undefined && (items?.length ?? 0) === 0 && hasClientRow === false));

  const dismissWelcome = () => {
    document.cookie = "ct_library_welcome_dismissed=1; path=/; max-age=31536000";
    setDismissed(true);
  };

  async function download(productId: string) {
    try {
      const { url } = await sign({ data: { productId } });
      window.open(url, "_blank");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Download failed");
    }
  }

  const filtered = useMemo(() => {
    const list = (items ?? []).filter((r) => {
      const p = r.product as unknown as LibProduct | null;
      if (!p) return false;
      if (query && !p.title.toLowerCase().includes(query.toLowerCase())) return false;
      return true;
    });
    if (sort === "az") {
      list.sort((a, b) =>
        ((a.product as unknown as LibProduct).title).localeCompare(
          (b.product as unknown as LibProduct).title,
        ),
      );
    }
    return list;
  }, [items, query, sort]);

  const lastUpdated = useMemo(() => {
    if (!items || items.length === 0) return null;
    return items[0].granted_at;
  }, [items]);

  return (
    <div className="min-h-screen bg-cream-50">
      <Navbar />
      <section className="mx-auto max-w-6xl px-6 py-20">
        {showWelcome && (
          <div className="relative mb-10 border-l-4 border-l-bronze-600 border-y border-r border-bronze-600/20 bg-cream-50 p-6 pr-12">
            <button
              type="button"
              onClick={dismissWelcome}
              aria-label="Dismiss"
              className="absolute right-3 top-3 inline-flex items-center gap-1 font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500 hover:text-navy-800"
            >
              Dismiss <X size={12} />
            </button>
            <Eyebrow>Welcome to Currency Thoughts</Eyebrow>
            <h3 className="mt-3 font-serif text-2xl text-navy-900">Your Library is ready.</h3>
            <p className="mt-2 max-w-2xl font-sans text-[14px] leading-relaxed text-charcoal-500">
              This is where every resource you collect will live. Start by browsing
              what's available — or take our Legacy Score to see where your family
              stands.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <PrimaryButton onClick={() => (window.location.href = "/store")}>
                Browse the Store →
              </PrimaryButton>
              <SecondaryButton onClick={() => (window.location.href = "/tools/legacy-score")}>
                Take the Legacy Score →
              </SecondaryButton>
            </div>
          </div>
        )}
        <div className="flex items-end justify-between gap-6">
          <div>
            <Eyebrow>Your library</Eyebrow>
            <h1 className="mt-4 font-serif text-5xl text-navy-900">Your Library</h1>
            {items && items.length > 0 && (
              <p className="mt-3 font-sans text-[15px] text-charcoal-500">
                {items.length} resource{items.length === 1 ? "" : "s"} · last updated {relative(lastUpdated)}
              </p>
            )}
          </div>
          <Link
            to="/store/library/orders"
            className="font-sans text-xs uppercase tracking-[0.22em] text-bronze-600"
          >
            Purchase History →
          </Link>
        </div>

        {isLoading ? (
          <p className="mt-16 font-sans text-charcoal-500">Loading…</p>
        ) : !items || items.length === 0 ? (
          <div className="mt-16 flex justify-center">
            <div className="w-full max-w-[480px] rounded-sm border border-bronze-600/30 bg-white p-12 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-bronze-600/15 text-bronze-600">
                <BookOpen size={28} />
              </div>
              <h2 className="mt-6 font-serif text-2xl text-navy-900">Your Library is empty.</h2>
              <p className="mt-3 font-sans text-sm text-charcoal-500">
                Resources you purchase will live here — yours to download anytime.
              </p>
              <div className="mt-6 flex justify-center">
                <SecondaryButton onClick={() => (window.location.href = "/store")}>
                  Browse the Store →
                </SecondaryButton>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Input
                placeholder="Search your library…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="max-w-xs"
              />
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value as never)}
                className="rounded-sm border border-bronze-600/30 bg-white px-3 py-2 font-sans text-sm"
              >
                <option value="recent">Most recent</option>
                <option value="az">A–Z</option>
                <option value="category">Category</option>
              </select>
            </div>

            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((row) => {
                const p = row.product as unknown as LibProduct | null;
                if (!p) return null;
                const revoked = Boolean(
                  (row as { revoked_at?: string | null }).revoked_at,
                );
                return (
                  <article
                    key={row.id}
                    className={`flex flex-col rounded-sm border bg-white p-5 shadow-[0_4px_18px_-12px_rgba(168,139,92,0.25)] ${
                      revoked
                        ? "border-danger/40 opacity-75"
                        : "border-bronze-600/20"
                    }`}
                  >
                    {p.thumbnail_url ? (
                      <img
                        src={p.thumbnail_url}
                        alt=""
                        className={`aspect-[4/3] w-full rounded-sm object-cover ${revoked ? "grayscale" : ""}`}
                      />
                    ) : (
                      <div className="flex aspect-[4/3] w-full items-center justify-center rounded-sm bg-navy-900/5">
                        <Seal title="CT" subtitle="" size="sm" />
                      </div>
                    )}
                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-bronze-600">
                        {p.product_type.replace("-", " ")}
                      </p>
                      {revoked && (
                        <span className="rounded-sm border border-danger/40 px-2 py-[2px] font-sans text-[10px] font-semibold uppercase tracking-[0.18em] text-danger">
                          Access revoked
                        </span>
                      )}
                    </div>
                    <h3 className="mt-2 font-serif text-lg text-navy-900">{p.title}</h3>
                    {p.subtitle && (
                      <p className="mt-1 font-sans text-sm text-charcoal-500 line-clamp-2">
                        {p.subtitle}
                      </p>
                    )}
                    {revoked ? (
                      <p className="mt-4 font-sans text-xs leading-relaxed text-charcoal-500">
                        Access to this resource was withdrawn after a refund or
                        payment dispute. Your purchase record is preserved — contact
                        us if you believe this is an error.
                      </p>
                    ) : (
                      <p className="mt-4 font-sans text-xs text-charcoal-500">
                        Downloaded {row.download_count} time{row.download_count === 1 ? "" : "s"} · last{" "}
                        {relative(row.last_downloaded_at)}
                      </p>
                    )}
                    <div className="mt-5">
                      <PrimaryButton
                        onClick={() => download(p.id)}
                        className="w-full"
                        disabled={revoked}
                      >
                        {revoked ? "Unavailable" : "Download"}
                      </PrimaryButton>
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        )}
      </section>
      <Footer />
    </div>
  );
}